import { useCallback } from "react";
import type { FeatureTourViewerTopic } from "./feature-tour-data";
import type { FeatureTourStatuses, FeatureTourViewStatus } from "./fue-flags";
import { useFueFlags } from "./use-fue-flags";

type UseFeatureTours = {
  /** Whether the statuses, which feature tours the user has seen, have been loaded. */
  areVisibilityStatusesLoaded: boolean;

  /** Which feature tours the user has already skipped or completed. */
  visibilityStatuses: FeatureTourStatuses;

  /** Persistently remembers that the current user has skipped or completed a feature tour. */
  markStatus(
    topicId: FeatureTourViewerTopic,
    status: FeatureTourViewStatus,
  ): void;
};

/**
 * Loads and persists the status of the feature tour topics for the current user.
 *
 * The statuses are stored in the FUE flags of the user, so that they are shared across devices.
 *
 * @returns The statuses of all topics and a callback to remember that a tour was seen
 */
export function useFeatureTours(): UseFeatureTours {
  const { areFueFlagsLoaded, fueFlags, updateFueFlags } = useFueFlags();

  const markStatus = useCallback(
    (topicId: FeatureTourViewerTopic, status: FeatureTourViewStatus) => {
      updateFueFlags((current) => ({
        ...current,
        known: { ...current.known, [topicId]: status },
      }));
    },
    [updateFueFlags],
  );

  return {
    areVisibilityStatusesLoaded: areFueFlagsLoaded,
    visibilityStatuses: fueFlags.known,
    markStatus,
  };
}
